import { Box, Typography, CircularProgress, Paper } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { useStepper } from "../../../context/stepper-provider";
import { useNotification } from "../../../context/notification-provider";
import StepContentBox from "../components/shared/step-content-box";
import useClientSelection from "../hooks/use-client-selection";
import getMenuActions from "../utils/menu-actions";
import { useDataCleaningContext } from "../context/data-cleaning-context";
import MinimizedClientView from "../components/client/minimized-client-view";
import ExpandedClientView from "../components/client/expanded-client-view";

const SelectClientStep = () => {
  const { nextStep, completeStep, incompleteStep, goToStep, activeStep } =
    useStepper();
  const { setSelectedClients } = useDataCleaningContext();
  const { showNotification } = useNotification();
  const [isMinimized, setIsMinimized] = useState(false);

  const {
    selectAll,
    displayedClients,
    unselectedClients,
    selectedCount,
    selectedClientIds,
    isLoading,
    error,
    handleClientToggle,
    handleSelectAll,
    handleAddClient,
    handleClearSelection,
    refetch,
    clients,
  } = useClientSelection();

  const hasData = !isLoading && clients.length > 0;

  const handleShowResults = () => {
    if (selectedCount > 0) {
      setSelectedClients(selectedClientIds);
      setIsMinimized(true);
      requestAnimationFrame(() => {
        completeStep(activeStep);
        nextStep();
      });
    }
  };

  const handleClear = () => {
    handleClearSelection();
    setIsMinimized(false);
    incompleteStep(activeStep);
    goToStep(activeStep);
  };

  const handleExpand = () => {
    setIsMinimized(false);
  };

  const handleRefresh = () => {
    refetch();
    showNotification({
      message: "Clients refreshed successfully",
      severity: "success",
    });
  };

  const menuActions = getMenuActions({
    onRefresh: handleRefresh,
    onClearSelection: handleClear,
  });

  let expandAction;
  if (isMinimized) {
    expandAction = {
      icon: <ExpandMoreIcon />,
      onClick: handleExpand,
    };
  }

  if (isLoading) {
    return (
      <StepContentBox
        title="CLIENTS"
        menuActions={menuActions}
        expandAction={expandAction}
        contentPadding={2}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      </StepContentBox>
    );
  }

  if (error) {
    return (
      <StepContentBox
        title="CLIENTS"
        menuActions={menuActions}
        expandAction={expandAction}
        contentPadding={2}
      >
        <Box sx={{ py: 2 }}>
          <Typography color="error">
            Error loading clients. Please contact support or try refreshing.
          </Typography>
        </Box>
      </StepContentBox>
    );
  }

  // No data to clean
  if (!hasData) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: "grey.50",
          border: 1,
          borderColor: "grey.200",
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Data to Clean
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There is currently no data available that requires cleaning.
        </Typography>
      </Paper>
    );
  }

  if (isMinimized && selectedCount > 0) {
    const selectedClients = displayedClients.filter((c) => c.selected);

    return (
      <StepContentBox
        title="CLIENTS"
        menuActions={menuActions}
        expandAction={expandAction}
        contentPadding={2}
      >
        <MinimizedClientView
          selectedClients={selectedClients}
          selectedCount={selectedCount}
          onExpand={handleExpand}
        />
      </StepContentBox>
    );
  }

  return (
    <StepContentBox
      title="CLIENTS"
      menuActions={menuActions}
      expandAction={expandAction}
      contentPadding={2}
    >
      <ExpandedClientView
        displayedClients={displayedClients}
        unselectedClients={unselectedClients}
        selectAll={selectAll}
        selectedCount={selectedCount}
        onClientToggle={handleClientToggle}
        onSelectAll={handleSelectAll}
        onAddClient={handleAddClient}
        onShowResults={handleShowResults}
      />
    </StepContentBox>
  );
};

export default SelectClientStep;
